import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { CartProvider } from './contexts/CartContext';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import NearbyAppointmentAlert from './components/NearbyAppointmentAlert';
import Home from './pages/Home';
import Login from './pages/Login';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import Pharmacy from './pages/Pharmacy';
import Cart from './pages/Cart';
import MyOrders from './pages/MyOrders';
import Checkout from './pages/Checkout';
import PharmacistDashboard from './pages/PharmacistDashboard';
import Unauthorized from './pages/Unauthorized';
import OnlineConsultation from './pages/OnlineConsultation';
import DoctorList from './pages/DoctorList';
import BookAppointment from './pages/BookAppointment';
import InventoryManagement from './pages/InventoryManagement';
import AdminInventory from './pages/AdminInventory';
import DoctorProfile from './pages/DoctorProfile';
import Appointments from './pages/Appointments';
import Hospitals from './pages/Hospitals';
import Patients from './pages/Patients';
import Prescriptions from './pages/Prescriptions';
import Settings from './pages/Settings';
import Tenants from './pages/Tenants';
import Users from './pages/Users';
import Product from './pages/Product';
import DoctorDashboard from './pages/DoctorDashboard';
import DoctorSchedule from './pages/DoctorSchedule';
import ConsultationFormPage from './pages/ConsultationFormPage';
import ConsultationResultPage from './pages/ConsultationResultPage';
import ArticlePage from './pages/ArticlePage';
import CreateArticlePage from './pages/CreateArticlePage';
import AdminDoctors from './pages/AdminDoctors';
import AdminPatients from './pages/AdminPatients';
import AdminTenants from './pages/AdminTenants';
import ActiveMedicines from './pages/ActiveMedicines';
import PlatformLanding from './pages/PlatformLanding';
import TenantNotFound from './pages/TenantNotFound';

const HIDE_NAVBAR_PATHS = ['/login', '/unauthorized'];

const Layout = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const hide = HIDE_NAVBAR_PATHS.includes(location.pathname);
  return (
    <>
      {!hide && <Navbar />}
      {children}
      {!hide && isAuthenticated && <NearbyAppointmentAlert />}
    </>
  );
};

const RequireAuthWithRedirect = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!isAuthenticated) {
    const target = location.pathname + location.search;
    return <Navigate to="/login" replace state={{ from: target }} />;
  }
  return children;
};

// The tenant is fixed by the hostname (see TenantContext), so routes carry no
// tenant segment. Only rendered once a tenant has been resolved.
const TenantRoutes = () => {
  return (
    <Layout>
      <Routes>
        <Route path="/"                             element={<Home />} />
        <Route path="/services/pharmacy"            element={<Pharmacy />} />
        <Route path="/services/online-consultation" element={<OnlineConsultation />} />
        <Route path="/services/hospitals"            element={<Hospitals />} />
        <Route path="/services/doctors"             element={<DoctorList />} />

        <Route path="/cart"         element={<Cart />} />
        <Route path="/orders"       element={<MyOrders />} />
        <Route path="/checkout"     element={<Checkout />} />
        <Route path="/login"        element={<Login />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/pharmacist"   element={<PharmacistDashboard />} />

        {/* ── Dashboards ───────────────────────────────────────── */}
        <Route path="/dashboard/super-admin"  element={<PrivateRoute allowedRoles={['ADMIN']}><Dashboard /></PrivateRoute>} />
        <Route path="/dashboard/doctor"       element={<PrivateRoute allowedRoles={['DOCTOR']}><Dashboard /></PrivateRoute>} />
        <Route path="/doctor/dashboard"        element={<PrivateRoute allowedRoles={['DOCTOR']}><DoctorDashboard /></PrivateRoute>} />
        <Route path="/doctor/schedule"         element={<PrivateRoute allowedRoles={['DOCTOR']}><DoctorSchedule /></PrivateRoute>} />
        <Route path="/doctor/create-content"   element={<PrivateRoute allowedRoles={['DOCTOR']}><CreateArticlePage /></PrivateRoute>} />
        <Route path="/doctor/consultation/:appointmentId" element={<PrivateRoute allowedRoles={['DOCTOR']}><ConsultationFormPage /></PrivateRoute>} />
        <Route path="/doctor/consultation/:appointmentId/results" element={<PrivateRoute allowedRoles={['DOCTOR']}><ConsultationResultPage /></PrivateRoute>} />
        <Route path="/dashboard/patient"      element={<PrivateRoute allowedRoles={['PATIENT']}><Dashboard /></PrivateRoute>} />

        {/* ── Inventory ────────────────────────────────────────── */}
        <Route path="/dashboard/inventory" element={<PrivateRoute allowedRoles={['ADMIN']}><InventoryManagement /></PrivateRoute>} />
        <Route path="/inventory"           element={<PrivateRoute allowedRoles={['ADMIN']}><AdminInventory /></PrivateRoute>} />

        {/* ── Admin doctor/patient management ──────────────────── */}
        <Route path="/admin/doctors"   element={<PrivateRoute allowedRoles={['ADMIN']}><AdminDoctors /></PrivateRoute>} />
        <Route path="/admin/patients"  element={<PrivateRoute allowedRoles={['ADMIN']}><AdminPatients /></PrivateRoute>} />
        <Route path="/admin/tenants"   element={<PrivateRoute allowedRoles={['ADMIN']}><AdminTenants /></PrivateRoute>} />

        {/* ── Doctors ──────────────────────────────────────────── */}
        <Route path="/services/doctors/:doctorId/profile" element={<DoctorProfile />} />
        <Route path="/services/doctors/:doctorId/book"    element={<RequireAuthWithRedirect><BookAppointment /></RequireAuthWithRedirect>} />
        <Route path="/services/book-appointment"          element={<RequireAuthWithRedirect><BookAppointment /></RequireAuthWithRedirect>} />

        {/* ── Patient-facing ───────────────────────────────────── */}
        <Route path="/appointments"  element={<RequireAuthWithRedirect><Appointments /></RequireAuthWithRedirect>} />
        <Route path="/appointments/:appointmentId/results" element={<RequireAuthWithRedirect><ConsultationResultPage /></RequireAuthWithRedirect>} />
        <Route path="/prescriptions" element={<RequireAuthWithRedirect><Prescriptions /></RequireAuthWithRedirect>} />
        <Route path="/patient/medicines" element={<RequireAuthWithRedirect><ActiveMedicines /></RequireAuthWithRedirect>} />
        <Route path="/profile"       element={<RequireAuthWithRedirect><Profile /></RequireAuthWithRedirect>} />
        <Route path="/settings"      element={<RequireAuthWithRedirect><Settings /></RequireAuthWithRedirect>} />

        {/* ── Staff / Admin ─────────────────────────────────────── */}
        <Route path="/patients" element={<PrivateRoute allowedRoles={['DOCTOR', 'staff', 'ADMIN', 'system_admin', 'tenant_admin']}><Patients /></PrivateRoute>} />
        <Route path="/tenants"  element={<PrivateRoute allowedRoles={['ADMIN', 'system_admin']}><Tenants /></PrivateRoute>} />
        <Route path="/users"    element={<PrivateRoute allowedRoles={['ADMIN', 'system_admin', 'tenant_admin']}><Users /></PrivateRoute>} />

        {/* ── Product detail (public) ──────────────────────────── */}
        <Route path="/services/pharmacy/:productId" element={<Product />} />

        {/* ── Article / video / whitepaper detail (public) ─────── */}
        <Route path="/articles/:articleId" element={<ArticlePage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

// Root domain → platform landing; unknown subdomain → not-found; otherwise the app.
const TenantGate = () => {
  const { status } = useTenant();

  if (status === 'root') {
    return (
      <Routes>
        <Route path="/" element={<PlatformLanding />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  if (status === 'not-found') return <TenantNotFound />;

  return (
    <CartProvider>
      <AuthProvider>
        <TenantRoutes />
      </AuthProvider>
    </CartProvider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <TenantProvider>
        <TenantGate />
      </TenantProvider>
    </BrowserRouter>
  );
}

export default App;